import { useState } from "react";
import { message } from "antd";
import ProjectService from "../../../features/DeliverableView/services/DeliverableService";
import CoaService from "../../../features/CoaView/services/CoaService";
import TemplateService from "../../../features/TemplateView/services/TemplateService";
import { FilterKeyTypes } from "../../../shared/types/FilterKeys.types";
import {
  ACTIVE_TABS_KEY,
  MAX_RECORDS_FOR_SHEET,
} from "../../utils/CommonConstants";
import { DeliverableMenuSheet } from "../DeliverableMenuComponent/DeliverableMenuComponent.types";
import { FilterKey } from "./ExcelViewComponent.types";

const projectService: ProjectService = new ProjectService();
const coaService = new CoaService();
const templateService = new TemplateService();

/**
 * The function `isExportDisabled` checks if export is disabled based on the number of keys and the
 * total number of records available.
 * @param {string} key1 - A string representing the first key.
 * @param {string} key2 - The `key2` parameter is a string representing a key value.
 * @param {string} key3 - The `key3` parameter is a string representing a key value.
 * @param {number} totalRecordsAvailable - The total number of records available for export.
 * @returns a boolean value. It returns true if the conditions specified in the if statement are met,
 * and false otherwise.
 */
export const isExportDisabled = (
  key1: string,
  key2: string,
  key3: string,
  totalRecordsAvailable: number
) => {
  if (
    [key1, key2, key3].filter((key) => key.length !== 0).length === 1 &&
    totalRecordsAvailable >= MAX_RECORDS_FOR_SHEET
  ) {
    return true;
  }
  return false;
};

/**
 * The function prevents the default behavior of a navigation link click if the sheet name of the
 * project menu item matches the active tab in the URL search parameters.
 * @param {Event} e - The parameter `e` is an event object that represents the event that triggered the
 * function. It could be a click event, keypress event, etc.
 * @param {ProjectMenuDetails} projectMenuItem - The `projectMenuItem` parameter is an object that
 * represents a menu item in a project. It likely contains properties such as `sheetName`, which is the
 * name of the sheet associated with the menu item.
 * @param {URLSearchParams} searchParams - The `searchParams` parameter is of type `URLSearchParams`.
 * It represents the query parameters of a URL. It allows you to access and manipulate the query
 * parameters of a URL.
 */
export const onNaviagtionLinkClick = async (
  e: Event,
  projectMenuItem: DeliverableMenuSheet,
  searchParams: URLSearchParams
) => {
  if (projectMenuItem.sheetName === searchParams.get(ACTIVE_TABS_KEY)) {
    e.preventDefault();
  }
};

/**
 * The function `onCellHeightChange` takes in a row index, a new height value, and an array of heights,
 * and returns a new array with the updated height value at the specified row index.
 * @param {any} rowIdx - The `rowIdx` parameter represents the index of the row whose height is being
 * changed.
 * @param {any} newHeight - The `newHeight` parameter represents the new height value that you want to
 * set for a specific cell in a grid or table.
 * @param {any[]} heights - The `heights` parameter is an array that represents the heights of each
 * cell in a table. Each element in the array corresponds to the height of a specific row in the table.
 * @returns an array of heights.
 */
export const onCellHeightChange = (
  rowIdx: any,
  newHeight: any,
  heights: any[]
) => {
  const ch = [...heights];
  if (rowIdx > ch.length) {
    for (let i = ch.length; i <= rowIdx; i++) {
      ch.push(100);
    }
  }
  ch[rowIdx] = newHeight;
  return ch;
};

/**
 * The function `onCellWidthChange` takes in a column index, a new width, and an array of widths, and
 * returns a new array of widths with the updated width at the specified column index.
 * @param {any} columnIdx - The columnIdx parameter represents the index of the column whose width is
 * being changed.
 * @param {any} newWidth - The `newWidth` parameter is the new width value that you want to set for a
 * specific column in a table.
 * @param {any[]} widths - The `widths` parameter is an array that represents the current widths of the
 * cells in a table. Each element in the array corresponds to the width of a specific column in the
 * table.
 * @returns an array of column widths.
 */
export const onCellWidthChange = (
  columnIdx: any,
  newWidth: any,
  widths: any[]
) => {
  const cw = [...widths];
  if (columnIdx > cw.length) {
    for (let i = cw.length; i <= columnIdx; i++) {
      cw.push(100);
    }
  }
  cw[columnIdx] = newWidth;
  return cw;
};

/**
 * The function checks if a cell is read-only based on the header value at the specified coordinates.
 * @param {any} x - The x parameter represents the column index of the cell.
 * @param {any} y - The parameter `y` represents the row index of the cell.
 * @param {any[]} headerValues - The `headerValues` parameter is an array of arrays that represents the
 * header values of a table. Each inner array represents a row in the table, and each element in the
 * inner array represents a cell value in that row.
 * @returns a boolean value. If the value at `headerValues[0][x]` (where `x` is the column index) after
 * trimming and converting to lowercase is equal to "input", then it returns `false`. Otherwise, it
 * returns `true`.
 */
export const isCellReadOnly = (
  x: any,
  y: any,
  headerValues: any[],
  zoomLevel: number,
  headerRows: number
) => {
  if (
    !(zoomLevel < 0.7 || zoomLevel > 1.4) &&
    headerValues[0] &&
    headerValues[0].length >= x &&
    headerValues[0][x]?.trim().toLowerCase() === "input" &&
    // y >= 9
    y >= headerRows
  ) {
    return false;
  } else {
    return true;
  }
};

/**
 * The function takes in an array of Excel data, an array of changes, and two functions for determining
 * the column and row order, and returns the updated Excel data based on the changes.
 * @param {any[]} excelData - The `excelData` parameter is an array that represents the current data in
 * the Excel sheet. Each element in the array represents a row in the sheet, and each row is an array
 * of values representing the cells in that row.
 * @param {any[]} changes - The `changes` parameter is an array of objects representing the changes
 * made to the Excel data. Each object in the array has the following properties:
 * @param {any} getColumnOrder - The `getColumnOrder` parameter is a function that takes a column index
 * as input and returns the corresponding order of that column.
 * @param {any} getRowOrder - getRowOrder is a function that takes a parameter representing the row
 * index and returns the corresponding order of the row.
 * @returns the updated excel data after applying the changes.
 */
export const returnUpdatedExcelDataOnChange = (
  excelData: any[],
  changes: any[],
  getColumnOrder: any,
  getRowOrder: any
) => {
  const newData = [...excelData];
  for (const change of changes) {
    const cx = getColumnOrder(change.x);
    const cy = getRowOrder(change.y);
    if (!newData[cy]) {
      newData[cy] = [];
    }
    newData[cy][cx] = change.value;
  }
  return newData;
};

/**
 * The function `returnUpdatedExcelDataOnEdit` takes in an array of Excel data, a row index, and a JSON
 * object of Excel data, and returns the updated Excel data array.
 * @param {any[]} excelDataInput - An array containing the Excel data.
 * @param {any} y - The parameter `y` represents the index of the row in the `excelDataInput` array
 * that needs to be updated.
 * @param {any} jsonObjectOfExcelData - An object that represents the Excel data. Each key in the
 * object represents a row in the Excel sheet, and the value is an object that represents the values
 * and formulas in that row. The keys in the inner object represent the column index, and the value is
 * an object with two properties: "Value
 * @returns the input `excelDataInput` array.
 */
export const returnUpdatedExcelDataOnEdit = (
  excelDataInput: any[],
  y: any,
  jsonObjectOfExcelData: any
) => {
  const excelData = [...excelDataInput];
  let resultObject: any = {};
  excelData[y].forEach((value: any, index: number) => {
    resultObject[index] = { Value: value?.toString(), Formula: null };
  });
  let count = 0;
  for (let objKey in jsonObjectOfExcelData) {
    if (count === y - 10) {
      const updatedObject = Object.keys(resultObject).reduce(
        (acc: any, key) => {
          // Spread the values from the secondObject while keeping the formula
          acc[key] = {
            ...resultObject[key],
            Formula: jsonObjectOfExcelData[objKey].hasOwnProperty(key)
              ? jsonObjectOfExcelData[objKey][key].Formula
              : null,
          };
          return acc;
        },
        {}
      );
      jsonObjectOfExcelData[objKey] = updatedObject;
      break;
    }
    count = count + 1;
  }
  return excelDataInput;
};

//Handle selection changed of the cell
export const onSelectionChanged = (x1: any, y1: any, x2: any, y2: any) => {};

//Handle right click of the cell
export const onRightClick = () => {};

/**
 * The `onHandleExportClick` function exports data from a sheet displayed in allocation container,
 * creates a downloadable file, and opens it in a new tab.
 * @param {string} projectId - The `projectId` parameter is a string that represents the ID of the
 * project. It is used to identify the specific project for exporting data.
 * @param {string} sheetId - The `sheetId` parameter is the ID of the sheet that you want to export. It
 * is used to identify the specific sheet within a project.
 * @param {string} fileName - The `fileName` parameter is a string that represents the name of the file
 * that will be downloaded.
 * @param {string} [key1] - The `key1` parameter is an optional string parameter. It is used as a key
 * for filtering data during the export process.
 * @param {string} [key2] - The `key2` parameter is an optional string parameter. It is used as a key
 * for some operation or filtering in the `exportAxios` function. If provided, it will be passed as an
 * argument to the `exportAxios` function.
 * @param {string} [key3] - The `key3` parameter is an optional string parameter. It is used as a key
 * for the export operation.
 * @param {number} [currentPage] - The `currentPage` parameter is an optional parameter that represents
 * the current page number. It is used in the `exportAxios` function to specify the page number of the
 * data to be exported. If the `currentPage` parameter is not provided, all pages of data will be
 * exported.
 */
const downloadFile = (data: any, fileName: string) => {
  const blob = new Blob([data], {
    type: "application/octet-stream",
  });
  const url = URL.createObjectURL(blob);
  const aElement = document.createElement("a");
  aElement.setAttribute("download", fileName);
  aElement.href = url;
  aElement.setAttribute("target", "_blank");
  aElement.click();
  URL.revokeObjectURL(url);
};

export const onHandleTemplateExportClick = async (
  projectId: string,
  fileName: string,
  key1?: string,
  key2?: string,
  key3?: string,
  currentPage?: string
) => {
  try {
    const response = await templateService.exportTemplateAxios<any>(
      projectId,
      1,
      10,
      {
        Key1: key1,
        Key2: key2,
        Key3: key3,
        Key4: "",
      },
      currentPage?.toString() ?? ""
    );

    downloadFile(response, fileName);
  } catch (e: any) {
    message.error(e?.message || "Download Failed");
  }
};

export const onHandleCoaExportClick = async (
  projectId: string,
  fileName: string
) => {
  try {
    const response = await coaService.exportCoaAxios<any>(projectId);

    downloadFile(response, fileName);
  } catch (e: any) {
    message.error(e?.message || "Download Failed");
  }
};

export const onHandleExportClick = async (
  projectId: string,
  sheetId: string,
  fileName: string,
  FilterKeyTypes: FilterKeyTypes,
  rowCount: number,
  sheetnumber?: number | null,
  multisheet?: boolean,
) => {
  try {
    const response = await projectService.exportAxios<any>(
      projectId,
      sheetId,
      multisheet,
      FilterKeyTypes,
      rowCount,
      sheetnumber?.toString() ?? "null",
    );

    downloadFile(response, fileName);
  } catch (e: any) {
    message.error(e?.message || "Download Failed");
  }
};
/**
 * The function `returnSequenceArray` takes in an array of sequences and returns an object containing
 * sorted arrays of values from each sequence.This function is used to display the filter values in the
 * multi-sheet section
 * @param {any[]} sequence - The `sequence` parameter is an array of objects. Each object in the array
 * represents a set of values for a specific key. The `Values` property of each object contains an
 * array of values for that key.
 * @returns The function `returnSequenceArray` returns an object with three properties: `key1Data`,
 * `key2Data`, and `key3Data`. Each property contains an array of `FilterKey` objects.
 */
export const returnSequenceArray = (sequence: any[]) => {
  let key1s: FilterKey[] = [];
  let key2s: FilterKey[] = [];
  let key3s: FilterKey[] = [];
  const Key1Set = sequence[0]?.values;
  const key2Set = sequence[1]?.values;
  const key3Set = sequence[2]?.values;
  if (sequence.length > 0) {
    Key1Set?.forEach((x: any) => {
      key1s.push({ label: sequence[0]?.label, value: x });
    });
    key2Set?.forEach((x: any) => {
      key2s.push({ label: sequence[1]?.label, value: x });
    });
    key3Set?.forEach((x: any) => {
      key3s.push({ label: sequence[2]?.label, value: x });
    });
    return {
      key1Data: key1s.slice().sort((a: any, b: any) => a.value - b.value),
      key2Data: key2s.slice().sort((a: any, b: any) => a.value - b.value),
      key3Data: key3s.slice().sort((a: any, b: any) => a.value - b.value),
    };
  }
};

/* The above code is a TypeScript module that exports a function called `useOrderControl`. This
function is used to manage the order of columns and rows in a grid-like structure. */
export const DEFAULT_CELL_WIDTH = 10;
export const DEFAULT_CELL_HEIGHT = 2;
export function useOrderControl(initialColumns = [], initialRows = []) {
  const [columnOrder, setColumnOrder] = useState(initialColumns);
  const [rowOrder, setRowOrder] = useState(initialRows);

  const getColumnOrder = (x: number) => columnOrder[x] ?? x;
  const getRowOrder = (y: number) => rowOrder[y] ?? y;

  const onColumnOrderChange = (indices: number[], order: number) => {
    const co: any = [...columnOrder];

    const min = indices[0];
    const n = Math.max(
      order + indices.length,
      indices.reduce((a, b) => Math.max(a, b), 0)
    );
    while (co.length < n) co.push(co.length);

    co.splice(min, indices.length);
    co.splice(order, 0, ...indices.map((i) => getColumnOrder(i)));
    setColumnOrder(co);
  };

  const onRowOrderChange = (indices: number[], order: number) => {
    const ro: any = [...rowOrder];

    const min = indices[0];
    const n = Math.max(
      order + indices.length,
      indices.reduce((a, b) => Math.max(a, b), 0)
    );
    while (ro.length < n) ro.push(ro.length);

    ro.splice(min, indices.length);
    ro.splice(order, 0, ...indices.map((i) => getRowOrder(i)));
    setRowOrder(ro);
  };

  return { getColumnOrder, getRowOrder, onColumnOrderChange, onRowOrderChange };
}
